
import React from 'react';
import { Calendar, Cat, Book, ArrowLeft } from 'lucide-react';
import { BlogPost } from './types';
import BlogRelatedPosts from './BlogRelatedPosts';
import OptimizedImage from '../common/OptimizedImage';

type BlogPostDetailProps = {
  post: BlogPost;
  onBackClick: () => void;
  relatedPosts: BlogPost[];
};

const BlogPostDetail: React.FC<BlogPostDetailProps> = ({ post, onBackClick, relatedPosts = [] }) => {
  // Function to render the blog post content with markdown
  const renderContentSection = (content: string) => {
    if (!content) return null;
    
    // Split the content by lines
    const lines = content.split('\n');
    const renderedContent = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;

      // Handle headers
      if (line.startsWith('# ')) {
        renderedContent.push(<h1 key={`line-${i}`} className="text-3xl font-semibold text-gray-800 mt-8 mb-4">{line.substring(2)}</h1>);
      } else if (line.startsWith('## ')) {
        renderedContent.push(<h2 key={`line-${i}`} className="text-2xl font-semibold text-gray-800 mt-6 mb-3">{line.substring(3)}</h2>);
      } else if (line.startsWith('### ')) {
        renderedContent.push(<h3 key={`line-${i}`} className="text-xl font-semibold text-gray-800 mt-5 mb-2">{line.substring(4)}</h3>);
      }
      // Handle lists
      else if (line.startsWith('- ')) {
        renderedContent.push(
          <li key={`line-${i}`} className="ml-6 mb-2 text-gray-800">
            {line.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .split('<strong>').map((part, idx) => {
                if (idx % 2 === 0) {
                  return part;
                } else {
                  return <strong key={`part-${idx}`} className="font-semibold">{part.replace('</strong>', '')}</strong>;
                }
              })}
          </li>
        );
      }
      // Handle ordered lists (1., 2., etc.)
      else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s/);
        if (match) {
          const number = parseInt(match[1]);
          const content = line.replace(/^\d+\.\s/, '');
          
          renderedContent.push(
            <li key={`line-${i}`} value={number} className="ml-6 mb-2 list-decimal text-gray-800">
              {content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .split('<strong>').map((part, idx) => {
                  if (idx % 2 === 0) {
                    return part;
                  } else {
                    return <strong key={`${i}-${idx}`} className="font-semibold">{part.replace('</strong>', '')}</strong>;
                  }
                })}
            </li>
          );
        }
      }
      // Handle images with captions
      else if (line.startsWith('![') && line.includes('](/')) {
        const altText = line.substring(2, line.indexOf(']'));
        const imageUrl = line.substring(line.indexOf('](') + 2, line.indexOf(')'));
        
        renderedContent.push(
          <figure key={`line-${i}`} className="my-8">
            <OptimizedImage 
              src={imageUrl}
              alt={altText}
              className="rounded-lg w-full shadow-md hover:shadow-lg transition-shadow duration-300"
              width={800}
              height={600}
              priority={i < 2} // Prioritize loading the first two images
            />
            {altText && altText !== "image" && (
              <figcaption className="text-center text-sm text-gray-500 mt-2 italic">
                {altText}
              </figcaption>
            )}
          </figure>
        );
      }
      // Handle paragraphs
      else if (line.trim() !== '') {
        renderedContent.push(
          <p key={`line-${i}`} className="my-4 text-gray-800">
            {line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .split('<strong>').map((part, idx) => {
                if (idx % 2 === 0) {
                  return part;
                } else {
                  return <strong key={`${i}-${idx}`} className="font-semibold">{part.replace('</strong>', '')}</strong>;
                }
              })}
          </p>
        );
      }
    }

    return <div className="blog-content prose prose-lg max-w-none">{renderedContent}</div>;
  };

  if (!post) return null;

  // Ensure tags is an array
  const tags = Array.isArray(post.tags) ? post.tags : [];

  return (
    <div className="max-w-4xl mx-auto">
      <button 
        onClick={onBackClick}
        className="mb-6 flex items-center text-primary hover:text-primary-dark transition-colors group"
        aria-label="Back to all blog posts"
      >
        <ArrowLeft className="h-5 w-5 mr-1 transition-transform group-hover:-translate-x-1" />
        Back to all posts
      </button>

      <article className="bg-white rounded-xl shadow-sm p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
            <Calendar size={16} className="mr-1" />
            {post.date}
          </span>
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
            <Cat size={16} className="mr-1" />
            {post.category}
          </span>
          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
            <Book size={16} className="mr-1" />
            By {post.author}
          </span>
        </div>

        <div className="prose prose-lg max-w-none text-gray-800 prose-headings:font-semibold prose-headings:text-gray-800">
          {renderContentSection(post.content || '')}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium mb-3 text-gray-800">Tags:</h3>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag: string, index: number) => (
              <span key={`tag-${index}`} className="bg-gray-200 hover:bg-gray-300 transition-colors px-3 py-1 rounded-full text-sm cursor-pointer text-gray-800">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      <BlogRelatedPosts 
        currentPostId={post.id} 
        posts={relatedPosts}
        category={post.category}
      />
      
      <div className="mt-10 py-8 text-center">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Ready to Experience the History in Person?</h3>
        <p className="text-gray-600 mb-6">Join us for a unique walking tour to discover NYC's fascinating feline history.</p>
        <a 
          href="/booking" 
          className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-md font-medium transition-all duration-300"
        >
          Explore Our Tours
        </a>
      </div>
    </div>
  );
};

export default BlogPostDetail;
